import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/** Merge conditional class names, resolving Tailwind conflicts deterministically. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const RELATIVE_TIME_UNITS: readonly {
  limit: number;
  divisor: number;
  unit: Intl.RelativeTimeFormatUnit;
}[] = [
  { limit: 60, divisor: 1, unit: "second" },
  { limit: 3600, divisor: 60, unit: "minute" },
  { limit: 86400, divisor: 3600, unit: "hour" },
  { limit: 2592000, divisor: 86400, unit: "day" },
  { limit: 31536000, divisor: 2592000, unit: "month" },
  { limit: Infinity, divisor: 31536000, unit: "year" },
];

const relativeTimeFormatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

/**
 * Formats an ISO timestamp as a relative-time string, e.g. "3 hours ago" or
 * "in 2 days". Shared by any surface that needs relative-time display
 * (last-seen, age, deadline countdowns, ...).
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const deltaSeconds = (new Date(iso).getTime() - now.getTime()) / 1000;
  const absSeconds = Math.abs(deltaSeconds);
  const { divisor, unit } =
    RELATIVE_TIME_UNITS.find(({ limit }) => absSeconds < limit) ??
    RELATIVE_TIME_UNITS[RELATIVE_TIME_UNITS.length - 1]!;
  return relativeTimeFormatter.format(Math.round(deltaSeconds / divisor), unit);
}
